import * as React from 'react';

import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { InfrastructureMapContainer, InfrastructureMapTypes, MetroAndRoadsContainer } from './parts';
import { InfrastructureLayout, RouteButton } from '../../components/Infrastructure';
import {
  IInfrastructureProps,
  trackInfrastructureMapExist,
  trackInfrastructureMapShow,
  trackZoomControlClick,
} from './utils';
import { toGeoObjects } from './helpers';
import { useGeoObjectBalloon, useRouteButtonProps, useFullscreenProps, usePanoramaProps } from './hooks';
import { VIEWED_TIMEOUT } from '../../constants/viewedBlock';

export const InfrastructureContainer: React.FC<IInfrastructureProps> = ({
  mapRef,
  isMapLoaded,
  currentTypes,
  metroStations,
  roads,
  infrastructureByYearRef,
  centeredCoordinates,
  infrastructureTypes,
  setInfrastructureType,
  handleMapLoaded,
  onGeoObjectsFetch,
  controls,
  suggestPosition,
}) => {
  const { isPanorama, handlePanoramaSuccessEnter, handleNoPanoramaEnter, handlePanoramaExit } = usePanoramaProps();
  const { startBuildingRoute, isRouteMode, handleRouteModeExit, handleRouteModeEnter, handleRouteSuccess } =
    useRouteButtonProps({ mapRef, centeredCoordinates });

  const isVisible = !isPanorama && !isRouteMode;

  const { isFullscreenOpen, handleFullscreenEnter, handleFullscreenExit } = useFullscreenProps();
  const { onBalloonHtmlFirstFetch, onBalloonOpen } = useGeoObjectBalloon({
    infrastructureByYearRef,
    startBuildingRoute,
  });

  React.useEffect(() => {
    if (isMapLoaded) {
      const geoObjects = toGeoObjects({
        infrastructureByYear: infrastructureByYearRef.current,
        year: new Date().getFullYear(),
      });
      const geoObjectsByType = geoObjects.filter(item => currentTypes.includes(item.name));

      mapRef.current?.removeAllGeoObjects();
      mapRef.current?.addGeoObjects(geoObjectsByType);
    }
  }, [currentTypes]);

  React.useEffect(() => {
    trackInfrastructureMapExist();
  }, []);

  return (
    <ActionAfterViewObserver callback={trackInfrastructureMapShow} callbackDelay={VIEWED_TIMEOUT}>
      <InfrastructureLayout
        isDesktop={true}
        metroAndRoads={
          <MetroAndRoadsContainer key="infrastructure-map-metro" metroStations={metroStations} roads={roads} />
        }
      >
        <InfrastructureMapContainer
          mapRef={mapRef}
          controls={controls}
          onGeoObjectsFetch={onGeoObjectsFetch}
          onBalloonHtmlFetch={onBalloonHtmlFirstFetch}
          onMapLoaded={handleMapLoaded}
          onBalloonOpen={onBalloonOpen}
          centeredCoordinates={centeredCoordinates}
          geoObjectNames={currentTypes}
          suggestPosition={suggestPosition}
          onFullscreenEnter={handleFullscreenEnter}
          onFullscreenExit={handleFullscreenExit}
          onZoomControlClick={trackZoomControlClick}
          onRoutingStart={handleRouteModeEnter}
          onRoutingEnd={handleRouteModeExit}
          onRouteBuildingSuccess={handleRouteSuccess}
          onNoPanoramaEnter={handleNoPanoramaEnter}
          onPanoramaExit={handlePanoramaExit}
          onPanoramaSuccessEnter={handlePanoramaSuccessEnter}
        />

        {isVisible && (
          <>
            <RouteButton key="route-button" fixed={isFullscreenOpen} onClick={startBuildingRoute} />

            <InfrastructureMapTypes
              key="infrastructure-map-types"
              fixed={isFullscreenOpen}
              types={infrastructureTypes}
              currentTypes={currentTypes}
              onTypesChange={setInfrastructureType}
            />
          </>
        )}
      </InfrastructureLayout>
    </ActionAfterViewObserver>
  );
};
